/**
 * TESTAR UMA OS pelo ID (frente 6 de 11/09) — o pedido literal do Matheus.
 *
 * 🔴 Três botões, e a ordem importa: **Ver o texto** (o padrão — NÃO envia; é
 * o que ele usa 90% das vezes), **Mandar no grupo de teste**, e **Mandar no
 * grupo do cliente** — este último com confirmação explícita dizendo o nome
 * do grupo e do cliente, porque mensagem em grupo de cliente não se apaga.
 */
import { useState } from 'react';

import { Alert, Badge, Button, HelpTip, Input } from '@/components/ui';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { api, ApiError } from '@/lib/api';

interface ResultadoDoTeste {
  taskId: string;
  original: string;
  reescrito: string;
  iaUsada: boolean;
  iaMotivo: string | null;
  pontos: Array<{ rotulo: string; porque: string }>;
  texto: string;
  cliente: string | null;
  alvos: Array<{ grupoId: string; nome: string; porSupervisao: boolean; porTipo?: boolean }>;
  enviado: boolean;
  enviados?: number;
  /** Grupos em que a mesma OS já tinha saído — o "nunca duas vezes" barrou (BUGS #621). */
  jaTinhamSaido?: number;
  detalhe?: string;
}

export function TesteDeTarefa() {
  const confirm = useConfirmDialog();
  const [taskId, setTaskId] = useState('');
  const [r, setR] = useState<ResultadoDoTeste | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [ultimoDestino, setUltimoDestino] = useState<'ver' | 'teste' | 'cliente' | null>(null);

  const rodar = async (destino: 'ver' | 'teste' | 'cliente') => {
    if (!taskId.trim()) {
      setErro('Informe o ID da tarefa do Auvo.');
      return;
    }
    if (destino === 'cliente') {
      const alvosReais = r?.alvos?.filter((a) => !a.porSupervisao) ?? [];
      const ok = await confirm({
        title: 'Mandar no grupo do cliente — de verdade',
        message: `A mensagem da tarefa ${taskId.trim()} vai para ${
          r?.cliente ?? 'o cliente da tarefa'
        }, nos grupos: ${alvosReais.map((a) => a.nome).join(', ') || 'os grupos vinculados'} (e supervisão). Mensagem enviada em grupo de cliente não se apaga. Confirmar?`,
        confirmLabel: 'Mandar ao cliente',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    setErro(null);
    setOcupado(destino);
    try {
      const resp = await api.post<ResultadoDoTeste>('/api/v1/admin/whatsapp/teste-tarefa', {
        taskId: taskId.trim(),
        destino,
      });
      setR(resp);
      setUltimoDestino(destino);
    } catch (e) {
      const corpo = e instanceof ApiError ? (e.response as { erro?: string } | undefined) : undefined;
      setErro(corpo?.erro ?? (e instanceof Error ? e.message : 'Não deu para montar a mensagem.'));
    } finally {
      setOcupado(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="wa-teste-tarefa">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Testar o resumo de uma OS
        </span>
        <HelpTip texto="Cole o ID de uma tarefa do Auvo e veja a mensagem exatamente como ela sairia — com a reescrita da IA e os pontos de atenção. Ver o texto não envia nada; os dois botões de envio contam no teto do dia e ficam na trilha, marcados como teste." />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={taskId}
          onChange={(e) => setTaskId(e.target.value)}
          placeholder="ID da tarefa (ex.: 73891755)"
          className="w-56"
          data-testid="teste-tarefa-id"
        />
        <Button size="sm" loading={ocupado === 'ver'} onClick={() => void rodar('ver')} data-testid="teste-tarefa-ver">
          Ver o texto
        </Button>
        <Button
          size="sm"
          variant="secondary"
          loading={ocupado === 'teste'}
          disabled={!r}
          onClick={() => void rodar('teste')}
          title={r ? undefined : 'Primeiro veja o texto — enviar sem ler é como o erro acontece.'}
        >
          Mandar no grupo de teste
        </Button>
        <Button
          size="sm"
          variant="destructive"
          loading={ocupado === 'cliente'}
          disabled={!r}
          onClick={() => void rodar('cliente')}
          title={r ? undefined : 'Primeiro veja o texto — enviar sem ler é como o erro acontece.'}
        >
          Mandar no grupo do cliente
        </Button>
      </div>

      {erro && <Alert variant="danger" data-testid="teste-tarefa-erro">{erro}</Alert>}

      {r && (
        <div className="space-y-3">
          {r.enviado && (
            <Alert variant="info">Enviada{r.enviados ? ` a ${r.enviados} grupo(s)` : ''}. {r.detalhe ?? ''}</Alert>
          )}
          {ultimoDestino === 'cliente' && !r.enviado && (
            <Alert variant="warning" data-testid="teste-tarefa-nao-saiu">
              {r.jaTinhamSaido ? 'Não saiu de novo: esta OS já tinha ido a esses grupos.' : 'Não saiu. O motivo está no histórico do robô.'}
            </Alert>
          )}
          <div className="grid gap-3 lg:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                O que o técnico escreveu
              </p>
              <pre className="whitespace-pre-wrap rounded-lg border border-line p-3 text-sm dark:border-line-dark">{r.original}</pre>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                Como a mensagem sai {r.iaUsada ? '(reescrita pela IA)' : '(sem IA)'}
              </p>
              <pre className="whitespace-pre-wrap rounded-lg border border-line p-3 text-sm dark:border-line-dark" data-testid="teste-tarefa-texto">{r.texto}</pre>
              {r.iaMotivo && (
                <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">{r.iaMotivo}</p>
              )}
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Pontos de atenção que dispararam
            </p>
            {r.pontos.length === 0 ? (
              <p className="text-sm text-ink-3 dark:text-ink-dark-3">
                Nenhum — o questionário desta OS não marcou gatilho algum.
              </p>
            ) : (
              <ul className="space-y-1">
                {r.pontos.map((pt, i) => (
                  <li key={i} className="text-sm">
                    <strong>{pt.rotulo}</strong>{' '}
                    <span className="text-ink-3 dark:text-ink-dark-3">— disparou por {pt.porque}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Para onde iria de verdade
            </p>
            <div className="flex flex-wrap gap-1.5">
              {r.alvos.length === 0 ? (
                <span className="text-sm text-ink-3 dark:text-ink-dark-3">
                  Nenhum grupo — a mensagem não sai, e o motivo fica na trilha.
                </span>
              ) : (
                r.alvos.map((a) => (
                  <Badge key={a.grupoId} variant={a.porSupervisao ? 'neutral' : 'info'}>
                    {a.nome}
                    {a.porSupervisao ? ' · supervisão' : a.porTipo ? ' · pelo tipo da tarefa' : ''}
                  </Badge>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
